import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Unstable_Grid2';

import { DashboardT } from 'business/definitions';
import ModalForm from 'components/Form/ModalForm';
import { useCreateDashboard } from 'routes/AllDashboards/hooks/createDashboard';
import { useDuplicateDashboard } from 'routes/AllDashboards/hooks/duplicateDashboard';
import { useFetchActiveDashboards } from 'routes/AllDashboards/hooks/getDashboardsList';
import { DashboardTile } from 'routes/Home/ui/Tile/CommonTiles';
import { Tile } from 'routes/Home/ui/Tile/Tile';

type CreateDashboardModalProps = {
    isOpen: boolean;
    onCreate?: (arg1: DashboardT) => void;
    onClose: () => void;
};
const EMPTY_DASHBOARD = 'EMPTY_DASHBOARD';

const StyDashboardContainer = styled(Dialog, {
    name: 'StyDashboardContainer',
})({
    '& .MuiDialog-paperWidthMd': {
        maxWidth: '720px',
    },
});

const StyDialogTitle = styled(DialogTitle, {
    name: 'StyDialogTitle',
})({
    '& + .MuiDialogContent-root': {
        backgroundColor: '#f3f3f3',
        padding: '1rem',
    },
});

const CreateDashboardModal = ({
    isOpen,
    onCreate,
    onClose: onCloseProp,
}: CreateDashboardModalProps): JSX.Element => {
    const { t } = useTranslation();
    const [showDashboardSelectionDialog, setShowDashboardSelectionDialog] =
        useState(true);
    const [selectedDashboard, setSelectedDashboard] = useState(EMPTY_DASHBOARD);

    const { data: activeDashboards } = useFetchActiveDashboards();
    const templateDashboards = activeDashboards
        ? activeDashboards.filter((dashboard) => dashboard.isTemplate)
        : [];

    const onClose = () => {
        setShowDashboardSelectionDialog(true);
        setSelectedDashboard(EMPTY_DASHBOARD);
        onCloseProp();
    };

    const duplicateDashboardMutation = useDuplicateDashboard(onCreate);
    const createDashboardFromTemplate = () => {
        if (selectedDashboard === EMPTY_DASHBOARD) {
            setShowDashboardSelectionDialog(false);
        } else {
            const selectedTemplateDashboard = templateDashboards.find(
                (dashboard) => dashboard.slug === selectedDashboard,
            );
            if (selectedTemplateDashboard) {
                duplicateDashboardMutation.mutate({
                    title: `${t('dashboards.newDashboardPrefix')} - ${
                        selectedTemplateDashboard.title
                    }`,
                    key: selectedTemplateDashboard.key,
                });
            }
            onClose();
        }
    };

    const createDashboardMutation = useCreateDashboard(onCreate);
    const createNewDashboard = ({ title }: { title: string }) => {
        createDashboardMutation.mutate({ title });
        onClose();
    };
    return (
        <div>
            <StyDashboardContainer
                open={isOpen && showDashboardSelectionDialog}
                onClose={onClose}
                aria-labelledby="form-dialog-title"
                maxWidth="lg"
                fullWidth
            >
                <StyDialogTitle>
                    {t('dashboards.createModalTitle')}
                </StyDialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        <Grid className="e2e-empty-dashboard-creation">
                            <Tile
                                borderColor={
                                    selectedDashboard === EMPTY_DASHBOARD
                                        ? '#003cac'
                                        : ''
                                }
                                onClick={() =>
                                    setSelectedDashboard(EMPTY_DASHBOARD)
                                }
                                title={t('dashboards.newDashboardPrefix')}
                            />
                        </Grid>

                        {templateDashboards.map((dashboard) => (
                            <Grid key={dashboard.slug}>
                                <DashboardTile
                                    borderColor={
                                        selectedDashboard === dashboard.slug
                                            ? '#003cac'
                                            : ''
                                    }
                                    onClick={() =>
                                        setSelectedDashboard(dashboard.slug)
                                    }
                                    title={dashboard.title}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={onClose}
                        className="e2e-create-modal-close"
                    >
                        {t('common.close')}
                    </Button>
                    <Button
                        onClick={createDashboardFromTemplate}
                        variant="contained"
                        className="e2e-create-modal-submit"
                        data-testid="create-submit"
                    >
                        {t('common.create')}
                    </Button>
                </DialogActions>
            </StyDashboardContainer>
            <ModalForm
                show={isOpen && !showDashboardSelectionDialog}
                closeModal={onClose}
                submit={createNewDashboard}
                submitText={t('common.save')}
                title={t('dashboards.create')}
                fields={[
                    {
                        key: 'title',
                        name: 'title',
                        label: t('common.title'),
                        type: 'text',
                        value: t('dashboards.newDashboardPrefix'),
                        validators: ['required'],
                    },
                ]}
                submitClass="e2e-modal-update-dashboard-button"
            />
        </div>
    );
};

export default CreateDashboardModal;
